// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import { localUser } from '../utils/localUser';

const BASE_URL: string = (process.env.REACT_APP_API_URL as string)

// Define a service using a base URL and expected endpoints
export const frioApi = createApi({
  reducerPath: 'frioApi',
  baseQuery: retry (
    fetchBaseQuery({ 
        baseUrl: BASE_URL, 
        prepareHeaders: (headers) => {
            const token = localUser.accessToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`)
            }
        },
    }),{
        maxRetries: 1, //Hace dos reintentos antes de largar el error
    },
  ),
  keepUnusedDataFor: 60, //Cantidad de tiempo que los datos quedan en cache (60 segundos)
  //refetchOnMountOrArgChange: true, //revalida la informacion cuando se monta el componente, pero la revalidacion la hace en background 
  //refetchOnMountOrArgChange: 10, //revalida la informacion cuando se monta el componente pero a los 10 segundos de la ultima vez que hizo un get satisfactorio, pero la revalidacion la hace en background 
  refetchOnFocus: true, //revalida la informacion cuando se pone el focus
  refetchOnReconnect: true, //revalida la informacion cuando se vuelve a conectar el servidor

  endpoints: (builder) => ({

    //Devuelve todas las plantas registradas
    getPlantasAll: builder.query({
      query: () => "/plantas",
      //extraOptions: {maxRetries: 3} Se pudee hacer en cada consulta en particular
    }),

    //Devuelve todos los frigorificos registradas
    getFriosAll: builder.query({
      query: () => "/frigorificosAll",
      //extraOptions: {maxRetries: 3} Se pudee hacer en cada consulta en particular
    }),

    //Devuelve todas las plantas del id del usuario 
    getPlantasByUser: builder.query({
        query: (id: string) => ({
            url: "/userRelPlanta",
            params: {
                userId: id
            },
        })
    }),

    //Devuelve todos los frigorificos del id del usuario y el id de la planta
    getFriosByUser: builder.query({
        query: (args) => ({
            url: "/userRelFrio",
            params: args,
        })
    }),

    //Devuelve los usuarios con todas las plantas y frigorificos registradas  
    getUsersPlantasFrios: builder.query({
        query: () => "/usersRel",
    }),

    //Devuelve todas las camaras  del id del frigorifico    
    getValorCamarasByFrioId:builder.query({
        query: (args) => ({
            url: "/valoresfrio",
            params: args,
        })
    }),

    getValoresByCamaraId:builder.query({
        query: (args) => ({
            url: "/valorescamara",
            params: args,
        })
    }),

    //Devuelve todos los frigorificos de la planta pasado por Id
    getFriosByPlantaId: builder.query({
        query: (id: string) => ({
            url: "/frigorificos",
             params: {
                plantaId: id
            },           
        })
      //extraOptions: {maxRetries: 3} Se pudee hacer en cada consulta en particular
    }),

    //Devuelve todas las camaras de la planta y el frigorifico pasados por Ids
    getCamarasByFrioId:builder.query({
        query: (args) => ({
            url: "/camaras",
            params: args,
        })
    }),

    getSensoresByCamaraId:builder.query({
        query: (id: string) => ({
            url: "/sensores",
            params: {
                camaraId: id
            },
        })
    }),


    agregarPlanta: builder.mutation({
        query: (nuevaPlanta) => ({
            url:"/createPlanta",
            method: "post",
            body: nuevaPlanta,
        })
    }),
    actualizarPlanta: builder.mutation({
        query: ({ id, ...rest }) => ({
            url:`updatePlanta/${id}`,
            method: "put",
            body: rest,
        })
    }),
    borrarPlanta: builder.mutation({
        query: (id) => ({
            url:`deletePlanta/${id}`,
            method: "delete",
        })
    }),

    agregarFrigorfico: builder.mutation({
        query: (nuevoFrigorifico) => ({
            url:"/createFrigorifico",
            method: "post",
            body: nuevoFrigorifico,
        })
    }),
    actualizarFrigorfico: builder.mutation({
        query: ({ id, ...rest }) => ({
            url:`updateFrigorifico/${id}`,
            method: "put",
            body: rest,
        })
    }),
    borrarFrigorfico: builder.mutation({
        query: (id) => ({
            url:`deleteFrigorifico/${id}`,
            method: "delete",
        })
    }),

    agregarCamara: builder.mutation({
        query: (nuevaCamara) => ({
            url:"/createCamara",
            method: "post",
            body: nuevaCamara,
        })
    }),
    actualizarCamara: builder.mutation({
        query: ({ id, ...rest }) => ({
            url:`updateCamara/${id}`,
            method: "put",
            body: rest,
        })
    }),
    borrarCamara: builder.mutation({
        query: (id) => ({
            url:`deleteCamara/${id}`,
            method: "delete",
        })
    }),

    agregarSensor: builder.mutation({
        query: (nuevoSensor) => ({
            url:"/createSensor",
            method: "post",
            body: nuevoSensor,
        })
    }),
    actualizarSensor: builder.mutation({
        query: ({ id, ...rest }) => ({
            url:`updateSensor/${id}`,
            method: "put",
            body: rest,
        })
    }),
    borrarSensor: builder.mutation({
        query: (id) => ({
            url:`deleteSensor/${id}`,
            method: "delete",
        })
    }),

    agregarUsuario: builder.mutation({
        query: (nuevoUsuario) => ({
            url:"/createUser",
            method: "post",
            body: nuevoUsuario,
        }),
        extraOptions : {maxRetries: 0}
    }),
    actualizarUsuario: builder.mutation({
        query: ({ id, ...rest }) => ({
            url:`updateUser/${id}`,
            method: "put",
            body: rest,
        })
    }),
    borrarUsuario: builder.mutation({
        query: (id) => ({
            url:`deleteUser/${id}`,
            method: "delete",
        })
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { 
    useGetPlantasAllQuery, 
    useGetFriosAllQuery,
    useGetPlantasByUserQuery, 
    useGetFriosByUserQuery, 
    useGetUsersPlantasFriosQuery,
    useGetValorCamarasByFrioIdQuery,
    useGetValoresByCamaraIdQuery,
    useLazyGetValoresByCamaraIdQuery,
    useGetFriosByPlantaIdQuery,
    useGetCamarasByFrioIdQuery,
    useGetSensoresByCamaraIdQuery,

    useAgregarPlantaMutation,
    useActualizarPlantaMutation,
    useBorrarPlantaMutation,

    useAgregarFrigorficoMutation,
    useActualizarFrigorficoMutation,
    useBorrarFrigorficoMutation,

    useAgregarCamaraMutation,
    useActualizarCamaraMutation,
    useBorrarCamaraMutation,

    useAgregarSensorMutation,
    useActualizarSensorMutation,
    useBorrarSensorMutation,

    useAgregarUsuarioMutation,
    useActualizarUsuarioMutation,
    useBorrarUsuarioMutation,
} = frioApi